import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { useMount } from "react-use";
import { toast } from "sonner";

import SmartlistForm from "./SmartlistForm";
import { SmartlistType } from "../../api/MediaAPI.types";
import SmartlistAPI from "../../api/SmartlistAPI";
import "../../css/admin/smartlist.scss";
import { getDefaultProjectLanguage } from "../../utils/data/Content";
import { smartlistToApi } from "../../utils/smartlist";
import { getSmartlistApiErrorMessageList } from "../../utils/translationAdapters/smartlist";
import { hasCoverPicture } from "../../utils/Translations";
import { isSmartlistAutoUpdated } from "../../utils/typePredicate";
import { GET_SMARTLIST_LIST_QUERY_KEY } from "../common/smartlist/GenericSmartlistList";
import { PROJECTS_ROUTE } from "../routes/routes";
import SmartlistAudioForm from "../smartlist_audio/SmartlistAudioForm";
import SmartlistCollectionsForm from "../smartlist_collections/SmartlistCollectionsForm";
import Spinner from "../utils/Spinner";

import type { Dispatch, JSX, SetStateAction } from "react";

import type {
  GetSmartlistResponse,
  SmartlistToCreate,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { AppState } from "../../reducers";

const ACTIONS = {
  EDIT: "edit",
  NEW: "new",
};

interface RenderSmarlistFormProps {
  smartlist?: GetSmartlistResponse | UpdatedSmartlist;
  setSmartlist: Dispatch<SetStateAction<UpdatedSmartlist>>;
  getSmartlistIsLoading: boolean;
  onUpdate: (updatedSmartlist: UpdatedSmartlist) => Promise<void>;
}

function RenderSmartlistForm({
  smartlist,
  setSmartlist,
  getSmartlistIsLoading,
  onUpdate,
}: RenderSmarlistFormProps): JSX.Element | null {
  const smartlistType = smartlist?.type ?? SmartlistType.STANDARD;

  if (getSmartlistIsLoading || !smartlist)
    return (
      <div className="admin-user-form_spinner-container">
        <Spinner />
      </div>
    );

  switch (smartlistType) {
    case SmartlistType.STANDARD:
    case SmartlistType.AUTO:
      return (
        <SmartlistForm
          isLoading={getSmartlistIsLoading}
          onSubmit={onUpdate}
          setSmartlist={setSmartlist}
          smartlist={smartlist}
        />
      );

    case SmartlistType.AUDIO:
      return (
        <SmartlistAudioForm
          isLoading={getSmartlistIsLoading}
          onSubmit={onUpdate}
          setSmartlist={setSmartlist}
          smartlist={smartlist}
        />
      );
    case SmartlistType.COLLECTION:
      return (
        <SmartlistCollectionsForm
          isLoading={getSmartlistIsLoading}
          onSubmit={onUpdate}
          setSmartlist={setSmartlist}
          smartlist={smartlist}
        />
      );
    default:
      return null;
  }
}

function SmartlistPage({
  smartlistCreationType,
}: {
  smartlistCreationType: Exclude<SmartlistType, SmartlistType.SEASON>;
}): JSX.Element {
  const { projectSlug, action, id } = useParams<{
    projectSlug: string;
    action: string;
    id: string;
  }>();

  const { t } = useTranslation("editor-smartlist");

  const history = useHistory();

  const queryClient = useQueryClient();

  const project = useSelector(
    (state: AppState) => state.architectureReducer.project,
  );

  const { language, language_uuid: languageUuid } = getDefaultProjectLanguage(
    null,
    project,
  );

  const [smartlist, setSmartlist] = useState<
    GetSmartlistResponse | UpdatedSmartlist
  >();

  const smartlistStandardOrAuto = [
    SmartlistType.STANDARD,
    SmartlistType.AUTO,
  ].includes(smartlistCreationType);

  const showSuccessMessage = (message: string): void => {
    toast.success(message);
  };

  const showErrorMessage = useCallback((message: string): void => {
    toast.error(message);
  }, []);

  const getSmartlistQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getSingleSmartlist,
    params: [{ smartlistId: id, projectSlug }],
  });

  const smartlistCreationSlug = useMemo(() => {
    switch (smartlistCreationType) {
      case SmartlistType.AUDIO:
        return "smartlist_audio";
      case SmartlistType.COLLECTION:
        return "collections";
      default:
        return "smartlist";
    }
  }, [smartlistCreationType]);

  const smartlistCreationLabel = useMemo(() => {
    switch (smartlistCreationType) {
      case SmartlistType.AUDIO:
        return t("newSmartlistAudio");
      case SmartlistType.COLLECTION:
        return t("newCollection");
      default:
        return t("newSmartlist");
    }
  }, [smartlistCreationType, t]);

  const { data: smartlistData, isLoading: getSmartlistIsLoading } = useQuery({
    ...getSmartlistQuery,
    onError: () => {
      showErrorMessage(t("editor-notif:fetchSmartlistError"));
      history.push(`${PROJECTS_ROUTE}/${projectSlug}/${smartlistCreationSlug}`);
    },
    enabled: !!id && action !== ACTIONS.NEW,
  });

  const defaultSmartlist = {
    label: smartlistCreationLabel,
    begin_date: moment().format(),
    end_date: null,
    translations: [
      {
        default: true,
        language,
        description: "",
        short_description: "",
        name: "",
      },
    ],
    translations_indexes: {
      language,
      language_uuid: languageUuid,
      default: { true: [0] },
    },
  };

  const getSmartlistToCreate = (): SmartlistToCreate => {
    if (smartlistCreationType === SmartlistType.AUTO) {
      return {
        ...defaultSmartlist,
        type: SmartlistType.AUTO,
        filter: {},
      };
    }

    return {
      ...defaultSmartlist,
      type: smartlistCreationType,
      filter: null,
    };
  };

  const createSmartlistMutation = useMutation({
    mutationFn: async (smartlistToCreate: SmartlistToCreate) =>
      SmartlistAPI.createSmartlist({
        data: smartlistToCreate,
        projectSlug,
      }),
    onSuccess: ({ uuid }) => {
      const getSmartlistIndexListQuery = SmartlistAPI.generateQuery({
        fn: SmartlistAPI.getSmartlistIndexList,
        params: [
          {
            projectSlug,
            smartlistType: smartlistStandardOrAuto
              ? [SmartlistType.STANDARD, SmartlistType.AUTO]
              : smartlistCreationType,
          },
        ],
      });

      queryClient.invalidateQueries({
        queryKey: getSmartlistIndexListQuery.queryKey,
      });

      showSuccessMessage(t("editor-notif:createSmartlistSuccess"));

      history.replace(
        `${PROJECTS_ROUTE}/${projectSlug}/${smartlistCreationSlug}/edit/${uuid}`,
      );
    },
    onError: () => {
      showErrorMessage(t("editor-notif:createSmartlistError"));
      history.replace(
        `${PROJECTS_ROUTE}/${projectSlug}/${smartlistCreationSlug}`,
      );
    },
  });

  const updateSmartlistMutation = useMutation({
    mutationFn: async ({
      uuid,
      data,
    }: {
      uuid: string;
      data: UpdatedSmartlist;
      onSuccess?: () => void;
    }) =>
      SmartlistAPI.updateSmartlist({
        uuid,
        data: smartlistToApi(data),
        projectSlug,
      }),
    onSuccess: (updatedSmartlist, { onSuccess }) => {
      showSuccessMessage(t("editor-notif:saveSuccess"));
      queryClient.setQueryData(getSmartlistQuery.queryKey, updatedSmartlist);
      queryClient.invalidateQueries([GET_SMARTLIST_LIST_QUERY_KEY]);
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      const errorMessages = getSmartlistApiErrorMessageList(error);
      if (errorMessages.length) {
        errorMessages.forEach(showErrorMessage);
      } else {
        showErrorMessage(t("editor-notif:saveError"));
      }
    },
  });

  const hasCreatedRef = useRef(false);

  useMount(() => {
    if (action === ACTIONS.NEW && !hasCreatedRef.current) {
      hasCreatedRef.current = true;
      createSmartlistMutation.mutate(getSmartlistToCreate());
    }
  });

  useEffect(() => {
    if (smartlistData) {
      setSmartlist(smartlistData);
    }
  }, [smartlistData]);

  const itemIsValid = useCallback(
    (item: UpdatedSmartlist, translationOnly?: boolean): string[] => {
      const errorMessages = [];

      if (!item.label || item.label.length <= 0) {
        errorMessages.push(t("editor-errors:noLabel"));
      }

      item.translations.forEach((translation) => {
        if (translation.default) {
          if (!translation.name || translation.name.length <= 0) {
            errorMessages.push(t("editor-errors:noDefaultTitle"));
          }

          if (!hasCoverPicture(translation)) {
            errorMessages.push(t("editor-errors:noDefaultPicture"));
          }
        }
      });

      if (translationOnly) return errorMessages;

      if (
        item.end_date &&
        moment(item.end_date).isBefore(moment(item.begin_date))
      ) {
        errorMessages.push(t("editor-errors:endBeforeStartDate"));
      }

      if (isSmartlistAutoUpdated(item)) {
        if (Object.keys(item.filter).length === 0) {
          errorMessages.push(t("editor-errors:smartlistCriteriaFilterEmpty"));
        } else {
          let errorFilter = false;

          Object.keys(item.filter).every((key) => {
            if (key === "castings") {
              if (item.filter[key]) {
                Object.keys(item.filter[key]).forEach((castingKey) => {
                  if (item.filter[key]?.[castingKey as unknown as number]) {
                    errorFilter = (
                      item.filter[key][
                        castingKey as unknown as number
                      ] as unknown as (string | null)[]
                    ).some((value) => !value);
                  }
                });
              }
            } else if (item.filter[key]) {
              errorFilter = item.filter[key].some((value) => !value);
            }
            return !errorFilter;
          });

          if (errorFilter) {
            errorMessages.push(
              t("editor-errors:smartlistCriteriaFilterMissing"),
            );
          }
        }
      }

      return errorMessages;
    },
    [t],
  );

  const onUpdate = useCallback(
    async (
      updatedSmartlist: UpdatedSmartlist,
      onSuccess?: () => void,
    ): Promise<void> => {
      const errorMessages = itemIsValid(updatedSmartlist);
      if (errorMessages.length === 0) {
        updateSmartlistMutation.mutate({
          uuid: updatedSmartlist.uuid,
          data: updatedSmartlist,
          onSuccess,
        });
      } else {
        const errorDetails = errorMessages.join(", ");
        showErrorMessage(
          `${t("editor-notif:saveError")}${
            errorDetails ? ` : ${errorDetails}` : ""
          }`,
        );
      }
    },
    [itemIsValid, updateSmartlistMutation, t, showErrorMessage],
  );

  return (
    <div
      className="editor-smartlist-update-container"
      data-testid="SmartlistPage"
    >
      <RenderSmartlistForm
        getSmartlistIsLoading={getSmartlistIsLoading}
        onUpdate={onUpdate}
        smartlist={smartlist}
        setSmartlist={
          setSmartlist as Dispatch<SetStateAction<UpdatedSmartlist>>
        }
      />
    </div>
  );
}

export default SmartlistPage;
